import type { BrowserStore } from '../platform/storage';
import { variantsToTeach } from '../content/tutorial';
import type { GameVariant } from '../../shared/config';

/**
 * First-play rules explainer. `explain()` resolves once the player accepts or dismisses;
 * acceptance is remembered per variant, so each variant's pages show once across every mode.
 */
export class RulesGate {
  open = $state(false);
  /** The round's variant; names the dialog. */
  variant = $state<GameVariant>('classic');
  /** The variants whose pages the open dialog shows, in order. */
  teaching = $state<GameVariant[]>([]);
  private readonly seen = new Set<GameVariant>();
  /** Offer keys already shown or found unnecessary; a dismissed offer does not return. */
  private readonly offered = new Set<string>();
  private offerOpen = false;
  private pending: ((accepted: boolean) => void) | null = null;

  constructor(private readonly store: Pick<BrowserStore, 'get' | 'set'>) {}

  explain = (variant: GameVariant): Promise<boolean> => {
    const teaching = variantsToTeach(
      variant,
      (candidate) => this.seen.has(candidate) || this.store.get(`rules.${candidate}`) === '1',
    );
    if (!teaching.length) return Promise.resolve(true);
    if (this.pending) return Promise.resolve(false);
    this.variant = variant;
    this.teaching = teaching;
    this.open = true;
    return new Promise((resolve) => {
      this.pending = resolve;
    });
  };

  /**
   * Teach `variant` to a player who waits for someone else to start the round. Each `key`
   * (room and variant) is offered once. An offer does not interrupt an open explanation.
   */
  offer = (variant: GameVariant, key: string): void => {
    if (this.pending || this.offered.has(key)) return;
    this.offered.add(key);
    void this.explain(variant);
    this.offerOpen = this.open;
  };

  /** Close an open offer whose lobby moved on, so it never covers a ready check or a round. */
  withdraw = (): void => {
    if (this.offerOpen) this.close(false);
  };

  close = (accepted = false): void => {
    if (accepted) {
      for (const variant of this.teaching) {
        this.seen.add(variant);
        this.store.set(`rules.${variant}`, '1');
      }
    }
    const resolve = this.pending;
    this.pending = null;
    this.offerOpen = false;
    this.open = false;
    resolve?.(accepted);
  };

  dispose = (): void => this.close(false);
}
